import React, { useState } from "react";
import api, { formatDateTime, formatTL } from "@/lib/api";
import { useLiveQuery, TAGS, invalidate } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plate, COUNTRY_META } from "@/components/Plate";
import { toast } from "sonner";
import { Warning, Plus, PencilSimple } from "@phosphor-icons/react";

const COUNTRY_OPTS = Object.entries(COUNTRY_META)
  .filter(([c]) => c !== "XX")
  .map(([c, m]) => ({ code: c, label: `${m.flag} ${m.name}` }));

export default function UnmatchedExits() {
  const [manualOpen, setManualOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [entryTime, setEntryTime] = useState("");

  // Direct manual entry state
  const [directOpen, setDirectOpen] = useState(false);
  const [dPlate, setDPlate] = useState("");
  const [dLot, setDLot] = useState("ust_kat");
  const [dCountry, setDCountry] = useState("TR");
  const [dEntryTime, setDEntryTime] = useState("");
  const [dNote, setDNote] = useState("");

  const { data: items } = useLiveQuery(
    TAGS.UNMATCHED,
    async () => (await api.get("/unmatched-exits?status=pending")).data,
    { intervalMs: 15000 },
  );
  const { data: manualHistory } = useLiveQuery(
    [TAGS.UNMATCHED, TAGS.SESSIONS],
    async () => (await api.get("/unmatched-exits/manual-entries-history?hours=48")).data,
    { intervalMs: 15000 },
  );
  const pending = items || [];
  const history = manualHistory || [];

  const openManual = (it) => {
    setSelected(it);
    // Default: 1 hour before exit
    const d = new Date(it.exit_time);
    d.setHours(d.getHours() - 1);
    setEntryTime(d.toISOString().slice(0, 16));
    setManualOpen(true);
  };

  const openDirect = () => {
    const now = new Date();
    setDEntryTime(now.toISOString().slice(0, 16));
    setDPlate(""); setDLot("ust_kat"); setDCountry("TR"); setDNote("");
    setDirectOpen(true);
  };

  const applyDirect = async () => {
    if (!dPlate || dPlate.length < 4) return toast.error("Geçerli plaka girin");
    try {
      await api.post("/unmatched-exits/direct-manual-entry", {
        plate: dPlate.toUpperCase(),
        parking_lot_key: dLot,
        country_code: dCountry,
        entry_time: new Date(dEntryTime).toISOString(),
        note: dNote || null,
      });
      toast.success("Manuel giriş oluşturuldu — Canlı Otopark'a düştü");
      setDirectOpen(false);
      invalidate([TAGS.UNMATCHED, TAGS.SESSIONS, TAGS.LIVE_ACTIVITY]);
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const applyManual = async () => {
    try {
      await api.post(`/unmatched-exits/${selected.id}/manual-entry`, {
        plate: selected.plate,
        parking_lot_key: selected.parking_lot_key,
        entry_time: new Date(entryTime).toISOString(),
        note: "Manuel giriş",
      });
      toast.success("Manuel giriş oluşturuldu ve oturum kapatıldı");
      setManualOpen(false); setSelected(null);
      invalidate([TAGS.UNMATCHED, TAGS.SESSIONS]);
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const applyFlat = async (it) => {
    if (!window.confirm(`${it.plate} plakasına 'giriş kaydı yok' sabit ücreti uygulansın mı?`)) return;
    try {
      await api.post(`/unmatched-exits/${it.id}/apply-flat-fee`, {});
      toast.success("Sabit ücret uygulandı");
      invalidate([TAGS.UNMATCHED, TAGS.SESSIONS, TAGS.KASA]);
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const dismiss = async (it) => {
    if (!window.confirm(`Bu kayıt iptal edilsin mi (yoksay)?`)) return;
    try {
      await api.post(`/unmatched-exits/${it.id}/dismiss`, {});
      toast.success("İptal edildi");
      invalidate(TAGS.UNMATCHED);
    } catch { toast.error("Başarısız"); }
  };

  const promoteToEntry = async (ue) => {
    if (!window.confirm(
      `'${ue.plate}' plakası için yeni bir AÇIK giriş kaydı oluşturulacak.\n\n` +
      `Fuzzy önerileri gerçek eşleşme değilse ve bu gerçekten yeni bir araçsa kullanın.`
    )) return;
    try {
      const { data } = await api.post(`/unmatched-exits/${ue.id}/promote-to-entry`);
      toast.success(`Yeni giriş açıldı → ${data.plate}`);
      invalidate([TAGS.UNMATCHED, TAGS.SESSIONS, TAGS.LIVE_ACTIVITY]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yeni giriş oluşturulamadı");
    }
  };

  const matchFuzzy = async (ue, candidate) => {
    const msg = `OCR yanılması onayı:\n\n` +
      `Kameranın okuduğu:  ${ue.plate}\n` +
      `Eşleşecek plaka:     ${candidate.plate}\n` +
      `Giriş zamanı:        ${new Date(candidate.entry_time).toLocaleString("tr-TR")}\n` +
      `Karakter farkı:      ${candidate.distance} (%${candidate.similarity_percent} benzer)\n\n` +
      `Bu doğru mu? Eşleştirilirse ${candidate.plate} oturumu kapatılacak ve ücret hesaplanacak.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(
        `/unmatched-exits/${ue.id}/match-session/${candidate.session_id}`,
      );
      toast.success(
        `Eşleştirildi → ${data.matched_plate} · ${formatTL(data.fee)} · ${data.duration_minutes} dk`,
      );
      invalidate([TAGS.UNMATCHED, TAGS.SESSIONS, TAGS.KASA, TAGS.LIVE_ACTIVITY]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eşleştirme başarısız");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Uyarı</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Giriş Kaydı Yok</h1>
          <div className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Çıkış okunan ama giriş kaydı bulunmayan plakalar. Operatör manuel giriş oluşturabilir veya sabit &quot;giriş kaydı yok&quot; ücreti uygulayabilir.
          </div>
        </div>
        <Button
          onClick={openDirect}
          data-testid="ue-direct-manual-btn"
          className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold"
        >
          <Plus size={14} /> Manuel Giriş Oluştur
        </Button>
      </div>

      {pending.length === 0 ? (
        <Card className="rounded-sm border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Bekleyen kayıt yok 🎉
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map((it) => (
            <Card key={it.id} className="rounded-sm border-amber-400 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <Warning size={22} weight="fill" className="text-amber-600" />
                    <Plate>{it.plate}</Plate>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">
                      {it.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Çıkış: {formatDateTime(it.exit_time)}
                  </div>
                </div>
                {it.exit_image_url && (
                  <img src={it.exit_image_url} alt="Çıkış" className="w-32 h-20 object-cover rounded-sm mb-3 border border-border" />
                )}

                {/* FUZZY CANDIDATES — kamera OCR yanılması olabilecek yakın plakalar */}
                {Array.isArray(it.fuzzy_matches) && it.fuzzy_matches.length > 0 && (
                  <div className="mb-3 p-3 rounded-sm border-2 border-primary/40 bg-primary/5" data-testid={`ue-fuzzy-${it.id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-primary">
                        {it.phantom_entry_prevented ? "🛡️ Fantom Giriş Engellendi" : "⚡ Muhtemel OCR Yanılması"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {it.phantom_entry_prevented
                          ? "· Bu okuma bir OCR yanılmasıyla YENİ giriş olarak yorumlanmak üzereydi. İçeride yakın plaka(lar) var:"
                          : "· Kamera yanlış okumuş olabilir, aşağıdaki açık kayıtlardan biri eşleşiyor:"}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {it.fuzzy_matches.map((cand) => (
                        <div key={cand.session_id} className="flex items-center justify-between gap-3 p-2 rounded-sm bg-white border border-border flex-wrap">
                          <div className="flex items-center gap-3 flex-wrap">
                            <Plate country={cand.country_code}>{cand.plate}</Plate>
                            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">
                              {cand.distance === 1 ? "1 karakter fark" : `${cand.distance} karakter fark`} · %{cand.similarity_percent} benzer
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Giriş: {formatDateTime(cand.entry_time)}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {cand.entry_image_url && (
                              <img
                                src={cand.entry_image_url}
                                alt="Giriş"
                                className="w-16 h-10 object-cover rounded-sm border border-border cursor-pointer"
                                onClick={() => window.open(cand.entry_image_url, "_blank")}
                                title="Giriş fotoğrafını büyüt"
                              />
                            )}
                            <Button
                              size="sm"
                              data-testid={`ue-fuzzy-match-${cand.session_id}`}
                              onClick={() => matchFuzzy(it, cand)}
                              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-[10px] font-semibold h-7 px-3"
                            >
                              ✓ Eşleştir & Kapat
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                      <div className="text-[10px] text-muted-foreground italic">
                        OCR benzerliği tespit edildi — otomatik eşleşme YAPILMAZ, siz onaylayana kadar bekler.
                      </div>
                      {it.phantom_entry_prevented && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`ue-promote-entry-${it.id}`}
                          onClick={() => promoteToEntry(it)}
                          className="rounded-sm text-[10px] uppercase tracking-widest font-semibold h-7 px-3 border-blue-400 text-blue-700 hover:bg-blue-50"
                        >
                          🚗 Yeni Giriş Olarak Kaydet
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" data-testid={`ue-manual-${it.id}`} onClick={() => openManual(it)}
                          className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold">
                    Manuel Giriş Ekle
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`ue-flat-${it.id}`} onClick={() => applyFlat(it)}
                          className="rounded-sm uppercase tracking-widest text-xs font-semibold">
                    Sabit Ücret Uygula
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(it)}
                          className="rounded-sm uppercase tracking-widest text-xs font-semibold text-destructive">
                    Yoksay
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* SON 48 SAAT MANUEL GİRİŞ GEÇMİŞİ — Canlı Otopark ile senkron */}
      {history.length > 0 && (
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <PencilSimple size={18} weight="fill" className="text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">
              Manuel Oluşturulan Girişler <span className="text-muted-foreground normal-case">· Son 48 Saat ({history.length})</span>
            </h3>
          </div>
          <Card className="rounded-sm border-border">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {history.map((s) => {
                  const active = s.status === "active";
                  return (
                    <div key={s.id} className="flex items-center justify-between p-3 gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Plate country={s.country_code}>{s.plate}</Plate>
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">
                          {s.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}
                        </Badge>
                        {active ? (
                          <Badge className="rounded-sm bg-green-100 text-green-800 border-green-300 uppercase text-[10px] tracking-widest">
                            Canlı · Park Halinde
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="rounded-sm uppercase text-[10px] tracking-widest">
                            Çıktı
                          </Badge>
                        )}
                        {s.note && <span className="text-xs text-muted-foreground italic">{`"${s.note}"`}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground text-right">
                        <div>Giriş: {formatDateTime(s.entry_time)}</div>
                        {s.exit_time && <div>Çıkış: {formatDateTime(s.exit_time)}</div>}
                        {typeof s.fee === "number" && s.fee > 0 && <div className="font-mono font-bold text-foreground">{formatTL(s.fee)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manuel Giriş Zamanı</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {selected && <div className="text-sm">Plaka: <Plate country={selected.country_code}>{selected.plate}</Plate></div>}
            <div>
              <Label>Tahmini Giriş Zamanı</Label>
              <Input type="datetime-local" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} className="rounded-sm" data-testid="ue-entry-time" />
            </div>
            <Button data-testid="ue-manual-confirm" onClick={applyManual} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Oluştur & Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct manual entry dialog */}
      <Dialog open={directOpen} onOpenChange={setDirectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Manuel Giriş</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plaka</Label>
                <Input value={dPlate} onChange={(e) => setDPlate(e.target.value.toUpperCase())}
                       className="rounded-sm font-plate uppercase" placeholder="34ABC123"
                       data-testid="ue-direct-plate" />
              </div>
              <div>
                <Label>Ülke</Label>
                <Select value={dCountry} onValueChange={setDCountry}>
                  <SelectTrigger className="rounded-sm" data-testid="ue-direct-country"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRY_OPTS.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Otopark</Label>
                <Select value={dLot} onValueChange={setDLot}>
                  <SelectTrigger className="rounded-sm" data-testid="ue-direct-lot"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ust_kat">Üst Kat</SelectItem>
                    <SelectItem value="alt_kat">Alt Kat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Giriş Zamanı</Label>
                <Input type="datetime-local" value={dEntryTime}
                       onChange={(e) => setDEntryTime(e.target.value)}
                       className="rounded-sm" data-testid="ue-direct-time" />
              </div>
            </div>
            <div>
              <Label>Not</Label>
              <Input value={dNote} onChange={(e) => setDNote(e.target.value)}
                     className="rounded-sm" placeholder="Kamera arızası / kar / ..."
                     data-testid="ue-direct-note" />
            </div>
            <Button data-testid="ue-direct-confirm" onClick={applyDirect}
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Oluştur
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
